import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, Typography, Button, Card, Modal,
  Calendar, List, Select, notification,
  Spin, Descriptions, Radio,
} from 'antd';
import {
  InfoCircleOutlined, EditOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import PlusIcon from 'mdi-react/PlusIcon';
import moment from 'moment';
import 'moment/locale/pt-br';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import FilterIcon from 'mdi-react/FilterIcon';
import { useNavigate } from 'react-router-dom';
import {
  patch, get, put, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { IBGEState, IBGECity } from '../../../sharedTypes/IBGETypes';
import { EventDay, Event } from '../../../sharedTypes/EventTypes';
import { fetchAllStates, fetchCitiesByState } from '../../../dataSource/APILocalidadesIBGE';
import { getUserType, getUserPermissions } from '../../../utils/users';
import './ListEvents.scss';

const ListEvents = (): React.ReactElement => {
  moment.locale('pt-br');

  interface FormListEvents {
    state?: string;
    city?: string;
    date?: moment.Moment;
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const userType = getUserType();
  const userPermissions = getUserPermissions();

  const [loadingEventSearch, setLoadingEventSearch] = useState<boolean>(false);
  const [statesLoading, setStatesLoading] = useState<boolean>(false);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [idsBeingCancelled, setIdsBeingCancelled] = useState<Array<number>>([]);
  const [detailsData, setDetailsData] = useState<Partial<Event>>({});
  const [detailsBeingFetched, setDetailsBeingFetched] = useState<Array<number>>([]);
  const [visibleModalDetails, setVisibleModalDetails] = useState<boolean>(false);
  const [statesArray, setStatesArray] = useState<Array<IBGEState>>([]);
  const [citiesArray, setCitiesArray] = useState<Array<IBGECity>>([]);
  const [eventsOnSelectedDate, setEventsOnSelectedDate] = useState<Array<EventDay>>([]);
  const [calendarDataSource, setCalendarDataSource] = useState<Array<EventDay>>([]);
  const [usesLocationFilter, setUsesLocationFilter] = useState<boolean>(false);
  const [pastDate, setPastDate] = useState<moment.Moment | undefined>(undefined);

  const handleSearchEvents = async (formValues: FormListEvents): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = patch({
      url: '/event/findAllByEventDay',

      params: {
        city: formValues?.city,
        finalDate: formValues?.date?.utc()?.endOf('month')?.toISOString(),
        initialDate: formValues?.date?.utc()?.startOf('month')?.toISOString(),
        state: formValues?.state,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      setEventsOnSelectedDate([]);
      setLoadingEventSearch(true);

      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    } finally {
      setLoadingEventSearch(false);
    }

    switch (true) {
      case (response?.body?.code === 15003):
        setCalendarDataSource(response?.body.eventDayOutputs);

        if (formValues.state != null || formValues.city != null) {
          setUsesLocationFilter(true);
        } else {
          setUsesLocationFilter(false);
        }

        if (formInstance.getFieldValue('date') != null) {
          const eventsOnSelectedDay = response?.body.eventDayOutputs
            .filter((element: EventDay) => {
              return element.eventDate === formInstance.getFieldValue('date')
                .format('YYYY-MM-DD');
            });

          setEventsOnSelectedDate(eventsOnSelectedDay);
        }
        break;

      case (responseError?.body?.code === -16000):
        notification.error({
          message: 'Erro',
          description: 'A data final não pode ser anterior a data inicial',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao pesquisar os eventos. Por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    const getStates = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        setStatesLoading(true);
        const requestRef: RequestWrapper = fetchAllStates();
        response = await requestRef
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }

        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar os Estados, por favor tente novamente mais tarde.',
        });
      }

      switch (true) {
        case (response?.statusCode === 200):
          setStatesArray(response?.body);
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um problema ao buscar os Estados, por favor tente novamente mais tarde.',
          });
          break;
      }

      setStatesLoading(false);
    };

    const firstSearch = async (): Promise<void> => {
      const currentMoment = moment();
      formInstance.setFieldsValue({
        date: currentMoment,
      });
      setPastDate(currentMoment);
      const formValues = formInstance.getFieldsValue();
      await handleSearchEvents(formValues);
    };

    const executeOnLoad = async (): Promise<void> => {
      await getStates();
      await firstSearch();
    };

    executeOnLoad();
    return () => {
      setStatesLoading(false);
    };
  }, []);

  const handleDateCellRendering = (cellDate: moment.Moment): React.ReactNode | null => {
    if (!calendarDataSource.length) {
      return null;
    }

    const hasEvent = calendarDataSource.filter((element: EventDay) => {
      return element.eventDate === cellDate.format('YYYY-MM-DD');
    });

    if (!hasEvent.length) {
      return null;
    }

    let finalCellBackgroundColor;
    const formDate = formInstance.getFieldValue('date');
    const formDateString = formDate?.format('YYYY-MM-DD');
    const cellDateString = cellDate.format('YYYY-MM-DD');

    switch (true) {
      case (
        usesLocationFilter === false
        && (formDateString !== cellDateString)
      ):
      case (
        usesLocationFilter === false
        && formDateString == null
      ):
        // cinza claro
        finalCellBackgroundColor = '#b2b2b269';
        break;

      case (
        usesLocationFilter === false
        && (formDateString === cellDateString)
      ):
        // cinza escuro
        finalCellBackgroundColor = '#b2b2b2';
        break;

      case (
        usesLocationFilter
        && (formDateString !== cellDateString)
      ):
      case (
        usesLocationFilter
        && formDateString == null
      ):
        // verde claro
        finalCellBackgroundColor = '#93bf4c78';
        break;

      case (
        usesLocationFilter
        && (formDateString === cellDateString)
      ):
        // verde escuro
        finalCellBackgroundColor = '#93bf4c';
        break;

      default:
        finalCellBackgroundColor = 'transparent';
        break;
    }

    return (
      <div
        className="ListEvents__CellColoredBakground"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: finalCellBackgroundColor,
        }}
      />
    );
  };

  const handleStateAddressChange = async (stateValue: string): Promise<void> => {
    formInstance.setFieldsValue({
      city: undefined,
    });

    if (!stateValue) {
      setCitiesArray([]);
      return;
    }

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      setCitiesLoading(true);
      const requestRef: RequestWrapper = fetchCitiesByState({
        identifier: statesArray.find((stateElem: IBGEState) => {
          return stateElem?.nome === stateValue;
        })?.sigla,
      });

      response = await requestRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.statusCode === 200):
        setCitiesArray(response?.body);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.error):
      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar as cidades do Estado selecionado.',
        });
        break;
    }

    setCitiesLoading(false);
  };

  const fetchEventDetails = async (eventID: number): Promise<void> => {
    if (!eventID) {
      throw new Error('id do evento não informado');
    }

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      if (detailsBeingFetched.includes(eventID) === false) {
        setDetailsBeingFetched((previous) => {
          return ([
            ...previous,
            eventID,
          ]);
        });
      }

      const requestConfigAndReference: RequestWrapper = get({
        url: `/event/findEventById/${eventID}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestConfigAndReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 15005):
        setDetailsData(response?.body.eventOutput);
        break;

      case (responseError?.body?.code === -16001):
        notification.error({
          message: 'Erro',
          description: 'Evento não encontrado',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar os detalhes do evento, tente novamente mais tarde por favor.',
        });
        break;
    }

    setDetailsBeingFetched((previous) => {
      if (previous.includes(eventID)) {
        return detailsBeingFetched.filter((elem) => elem !== eventID);
      }
      return previous;
    });
  };

  const showEventDetails = async (eventID: number): Promise<void> => {
    try {
      await fetchEventDetails(eventID);
      setVisibleModalDetails(true);
    } catch (error: unknown) {
      setVisibleModalDetails(false);
      setDetailsData({});
    }
  };

  const cancelEvent = async (eventID: number): Promise<void> => {
    if (!eventID) {
      throw new Error('id do evento não informado');
    }

    let cancelResponse: ResponseWrapper | undefined;
    let cancelError: ResponseWrapper | undefined;
    let genericCancelError: Error | undefined;

    try {
      if (idsBeingCancelled.includes(eventID) === false) {
        setIdsBeingCancelled((previous) => {
          return ([
            ...previous,
            eventID,
          ]);
        });
      }

      const requestConfigReference: RequestWrapper = put({
        url: `/event/cancelEvent/${eventID}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      cancelResponse = await requestConfigReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        cancelError = error.response;
      } else {
        genericCancelError = error as Error;
      }
    }

    switch (true) {
      case (cancelResponse?.body?.code === 15001): {
        notification.success({
          message: 'Sucesso',
          description: 'Evento cancelado.',
        });

        const cancelledEventData = eventsOnSelectedDate
          ?.[0]
          ?.eventDayDtos.find((elem) => {
            return elem.id === eventID;
          });

        calendarDataSource.forEach((elem) => {
          if (elem.eventDate === moment(cancelledEventData?.initialDate).utc().format('YYYY-MM-DD')) {
            elem.eventDayDtos.forEach((childElem) => {
              const newChildElem = childElem;
              if (childElem.id === eventID) {
                newChildElem.isCancelled = true;
              }
            });
          }
        });

        const newEventsOnSelectedDate = calendarDataSource
          .filter((element) => {
            return element.eventDate === moment(cancelledEventData?.initialDate).utc()
              .format('YYYY-MM-DD');
          });

        setCalendarDataSource([
          ...calendarDataSource,
        ]);

        setEventsOnSelectedDate(newEventsOnSelectedDate);
      } break;

      case (cancelError?.body?.code === -4001):
        break;

      case (cancelError?.body?.code === -4003):
        notification.error({
          message: 'Erro',
          description: 'Desculpe, você não tem permissão para realizar esta ação.',
        });
        break;

      case (cancelError?.body?.code === -16001):
        notification.error({
          message: 'Erro',
          description: 'Evento não encontrado.',
        });
        break;

      case (genericCancelError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao executar esta ação, tente novamente mais tarde por favor.',
        });
        break;
    }

    setIdsBeingCancelled((previous) => {
      if (previous.includes(eventID)) {
        return idsBeingCancelled.filter((elem) => elem !== eventID);
      }
      return previous;
    });
  };

  const showAddress = (): string => {
    let finalAddress = '';

    if (detailsData?.street) {
      finalAddress += detailsData.street;
    }

    if (detailsData?.number) {
      finalAddress += `, nº ${detailsData.number}`;
    }

    if (detailsData?.complement) {
      finalAddress += `, ${detailsData.complement}`;
    }

    if (detailsData?.district) {
      finalAddress += ` - ${detailsData.district}`;
    }

    if (detailsData?.city) {
      finalAddress += `. ${detailsData.city}`;
    }

    if (detailsData?.state) {
      finalAddress += ` - ${detailsData.state}`;
    }

    if (detailsData?.cep) {
      finalAddress += `. CEP: ${[detailsData?.cep.slice(0, 5), '-', detailsData?.cep.slice(5)].join('')}`;
    }

    return finalAddress;
  };

  return (
    <div
      className="ListEvents"
    >
      <Row
        className="ListEvents__ComponentRow"
        gutter={[24, 24]}
      >
        <Col
          className="ListEvents__ComponentColumn"
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
            className="ListEvents__PageTitleRow"
          >
            <Col
              className="ListEvents__PageTitleColumn"
            >
              <Typography.Title
                className="ListEvents__PageTitle"
              >
                Calendário
              </Typography.Title>
            </Col>

            <Col
              className="ListEvents__ButtonColumn"
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 12 }}
              xl={{ span: 10 }}
            >
              <Row
                gutter={24}
                justify="end"
                className="ListEvents__NestedButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 12 }}
                  lg={{ span: 12 }}
                  className="ListEvents__BackButtonColumn"
                >
                  <Button
                    type="default"
                    className="ListEvents__BackButton"
                    onClick={
                      (): void => {
                        navigate('/home');
                      }
                    }
                    block
                  >
                    Voltar
                  </Button>
                </Col>

                {
                  userType === 'COOPERCITRUS'
                    && (userPermissions || []).includes('Solicitar novo evento')
                    ? (
                      <Col
                        xs={{ span: 24 }}
                        sm={{ span: 12 }}
                        lg={{ span: 12 }}
                        className="ListEvents__NewEventButtonColumn"
                      >
                        <Button
                          type="primary"
                          icon={<PlusIcon className="ListEvents__NewEventButtonIcon" size={21} />}
                          block
                          className="ListEvents__NewEventButton"
                          onClick={
                            (): void => {
                              navigate('/events/new');
                            }
                          }
                        >
                          Cadastrar evento
                        </Button>
                      </Col>
                    )
                    : null
                }
              </Row>
            </Col>
          </Row>

          <Row
            gutter={[24, 24]}
            className="ListEvents__ContentRow"
          >
            <Col
              xs={{ span: 24 }}
              className="ListEvents__ContentColumn"
            >
              <Card
                className="ListEvents__Card"
              >
                <Form
                  layout="vertical"
                  className="ListEvents__Form"
                  form={formInstance}
                  onFinish={handleSearchEvents}
                  onFinishFailed={
                    (errorInfo): void => {
                      console.log('list events form finish failed', errorInfo);
                    }
                  }
                  onValuesChange={
                    async (changedValues, allValues): Promise<void> => {
                      if ('state' in changedValues) {
                        await handleStateAddressChange(changedValues.state);
                      }

                      if ('date' in changedValues) {
                        const eventsOnSelectedDay = calendarDataSource
                          .filter((element) => {
                            return element.eventDate === changedValues.date
                              .format('YYYY-MM-DD');
                          });

                        setEventsOnSelectedDate(eventsOnSelectedDay);

                        if (pastDate == undefined || !changedValues.date.isSame(pastDate, 'month')) {
                          await handleSearchEvents(formInstance.getFieldsValue());
                        }
                      }

                      setPastDate(allValues.date);
                    }
                  }
                >
                  <Row
                    gutter={[24, 24]}
                    className="ListEvents__FormRow"
                  >
                    <Col
                      xs={{ span: 24 }}
                      sm={{ span: 24 }}
                      md={{ span: 24 }}
                      lg={{ span: 12 }}
                      xl={{ span: 12 }}
                      className="ListEvents__FilterParentColumn"
                    >
                      <Row
                        gutter={[12, 24]}
                        className="ListEvents__FilterRow"
                      >
                        <Col
                          xs={{ span: 24 }}
                          sm={{ span: 24 }}
                          md={{ span: 24 }}
                          lg={{ span: 12 }}
                          xl={{ span: 7 }}
                          className="ListEvents__StateColumn"
                        >
                          <Form.Item
                            name="state"
                            className="ListEvents__StateFormItem"
                            label={(
                              <Typography.Text
                                className="ListEvents__StateLabel ListEvents__AllFormLabels"
                              >
                                Estados
                              </Typography.Text>
                            )}
                          >
                            <Select
                              className="ListEvents__StateInput"
                              allowClear
                              showSearch
                              loading={statesLoading}
                              options={
                                statesArray.map((element) => {
                                  return ({
                                    ...element,
                                    label: `${element.nome} (${element.sigla})`,
                                    value: element.nome,
                                  });
                                })
                              }
                              optionFilterProp="label"
                            />
                          </Form.Item>
                        </Col>

                        <Col
                          xs={{ span: 24 }}
                          sm={{ span: 24 }}
                          md={{ span: 24 }}
                          lg={{ span: 12 }}
                          xl={{ span: 7 }}
                          className="ListEvents__CityColumn"
                        >
                          <Form.Item
                            name="city"
                            className="ListEvents__CityFormItem"
                            label={(
                              <Typography.Text
                                className="ListEvents__CityLabel ListEvents__AllFormLabels"
                              >
                                Cidades
                              </Typography.Text>
                            )}
                            dependencies={['state']}
                          >
                            <Select
                              className="ListEvents__CityInput"
                              allowClear
                              showSearch
                              loading={citiesLoading}
                              options={
                                citiesArray.map((element) => {
                                  return ({
                                    ...element,
                                    label: element.nome,
                                    value: element.nome,
                                  });
                                })
                              }
                              optionFilterProp="label"
                            />
                          </Form.Item>
                        </Col>

                        <Col
                          xs={{ span: 24 }}
                          sm={{ span: 12 }}
                          md={{ span: 12 }}
                          lg={{ span: 12 }}
                          xl={{ span: 5 }}
                          className="ListEvents__ResetButtonColumn"
                        >
                          <Form.Item
                            label=" "
                            className="ListEvents__ResetButtonFormItem"
                          >
                            <Button
                              block
                              icon={(
                                <FilterRemoveIcon
                                  size={18}
                                  className="ListEvents__ResetButtonIcon"
                                />
                              )}
                              type="default"
                              htmlType="button"
                              className="ListEvents__ResetButton"
                              onClick={
                                (): void => {
                                  const current = moment();
                                  formInstance.resetFields();
                                  formInstance.setFieldsValue({
                                    date: current,
                                  });
                                  setUsesLocationFilter(false);
                                  setEventsOnSelectedDate([]);
                                  setCitiesArray([]);
                                  setPastDate(current);
                                  formInstance.submit();
                                }
                              }
                            >
                              Limpar
                            </Button>
                          </Form.Item>
                        </Col>

                        <Col
                          xs={{ span: 24 }}
                          sm={{ span: 12 }}
                          md={{ span: 12 }}
                          lg={{ span: 12 }}
                          xl={{ span: 5 }}
                          className="ListEvents__SubmitButtonColumn"
                        >
                          <Form.Item
                            label=" "
                            className="ListEvents__SubmitButtonFormItem"
                          >
                            <Button
                              block
                              icon={(
                                <FilterIcon
                                  size={18}
                                  className="ListEvents__SubmitButtonIcon"
                                />
                              )}
                              type="primary"
                              htmlType="submit"
                              className="ListEvents__SubmitButton"
                            >
                              Filtrar
                            </Button>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row
                        gutter={[24, 24]}
                        className="ListEvents__CalendarRow"
                      >
                        <Col
                          xs={{ span: 24 }}
                          className="ListEvents__CalendarColumn"
                        >
                          <Spin
                            spinning={loadingEventSearch}
                            className="ListEvents__CalendarSpin"
                          >
                            <Form.Item
                              name="date"
                              className="ListEvents__CalendarFormItem"
                              rules={[
                                {
                                  required: true,
                                  message: 'Por favor selecione uma data',
                                },
                              ]}
                            >
                              <Calendar
                                headerRender={
                                  ({
                                    value, type, onChange, onTypeChange,
                                  }): React.ReactNode => {
                                    const start = 0;
                                    const end = 12;
                                    const monthOptions = [];

                                    const current = value.clone();
                                    const localeData = value.localeData();
                                    const months = [];

                                    for (let i = 0; i < 12; i += 1) {
                                      current.month(i);
                                      const nameMonthLowerCase: string = localeData.monthsShort(current);
                                      const nameMonthFirstLetterUperCase: string = nameMonthLowerCase[0]?.toUpperCase() + nameMonthLowerCase.substr(1);
                                      months.push(nameMonthFirstLetterUperCase);
                                    }

                                    for (let index = start; index < end; index += 1) {
                                      monthOptions.push(
                                        <Select.Option
                                          className="ListEvents__MonthSelectOption"
                                          key={`${index}`}
                                        >
                                          {months[index]}
                                        </Select.Option>,
                                      );
                                    }

                                    const month = value.month();

                                    const year = value.year();
                                    const options = [];

                                    for (
                                      let yearItem = year - 10;
                                      yearItem < year + 10;
                                      yearItem += 1
                                    ) {
                                      options.push(
                                        <Select.Option
                                          key={yearItem}
                                          value={yearItem}
                                          className="ListEvents__YearSelectOption"
                                        >
                                          {yearItem}
                                        </Select.Option>,
                                      );
                                    }

                                    return (
                                      <div
                                        className="ListEvents__CalendarCustomHeader"
                                      >
                                        <Row
                                          gutter={8}
                                          className="ListEvents__CalendarCustomHeaderContentRow"
                                        >
                                          <Col
                                            xs={{ offset: 0 }}
                                            sm={{ offset: 3 }}
                                            md={{ offset: 11 }}
                                            lg={{ offset: 7 }}
                                            xl={{ offset: 13 }}
                                            className="ListEvents__CalendarCustomHeaderYearColumn"
                                          >
                                            <Select
                                              size="middle"
                                              dropdownMatchSelectWidth={false}
                                              className="ListEvents__CalendarCustomHeaderYearSelect"
                                              onChange={
                                                (newYear: string): void => {
                                                  const now = value
                                                    .clone()
                                                    .year(newYear as unknown as number);
                                                  onChange(now);
                                                }
                                              }
                                              value={String(year)}
                                            >
                                              {options}
                                            </Select>
                                          </Col>
                                          <Col
                                            className="ListEvents__CalendarCustomHeaderMonthColumn"
                                          >
                                            <Select
                                              className="ListEvents__CalendarCustomHeaderMonthSelect"
                                              size="middle"
                                              dropdownMatchSelectWidth={false}
                                              value={String(month)}
                                              onChange={
                                                (selectedMonth): void => {
                                                  const newValue = value.clone();
                                                  newValue.month(parseInt(selectedMonth, 10));
                                                  onChange(newValue);
                                                }
                                              }
                                            >
                                              {monthOptions}
                                            </Select>
                                          </Col>
                                          <Col
                                            className="ListEvents__CalendarCustomHeaderTypeRadioColumn"
                                          >
                                            <Radio.Group
                                              className="ListEvents__CalendarCustomHeaderTypeRadioGroup"
                                              size="middle"
                                              onChange={
                                                (e): void => {
                                                  onTypeChange(e.target.value);
                                                }
                                              }
                                              value={type}
                                            >
                                              <Radio.Button
                                                className="ListEvents__CalendarCustomHeaderTypeRadio"
                                                value="month"
                                              >
                                                Mês
                                              </Radio.Button>
                                            </Radio.Group>
                                          </Col>
                                        </Row>
                                      </div>
                                    );
                                  }
                                }
                                className="ListEvents__Calendar"
                                mode="month"
                                dateCellRender={handleDateCellRendering}
                              />
                            </Form.Item>
                          </Spin>
                        </Col>
                      </Row>
                    </Col>

                    <Col
                      xs={{ span: 24 }}
                      sm={{ span: 24 }}
                      md={{ span: 24 }}
                      lg={{ span: 12 }}
                      xl={{ span: 12 }}
                      className="ListEvents__ListColumn"
                    >
                      <List
                        header={(
                          <Typography.Text
                            className="ListEvents__EventsOnDateListTitle"
                          >
                            Eventos na data selecionada
                          </Typography.Text>
                        )}
                        className="ListEvents__EventsOnDateList"
                        itemLayout="horizontal"
                        dataSource={
                          eventsOnSelectedDate[0]
                            ?.eventDayDtos
                            ?.sort((elemA, elemB) => {
                              if (
                                moment(elemA.initialDate)
                                  .utc()
                                  .isBefore(moment(elemB.initialDate).utc())
                              ) {
                                return -1;
                              }

                              if (
                                moment(elemA.initialDate)
                                  .utc()
                                  .isAfter(moment(elemB.initialDate).utc())
                              ) {
                                return 1;
                              }

                              return 0;
                            })
                        }
                        bordered
                        split
                        size="large"
                        renderItem={
                          (item): React.ReactNode => {
                            const actionButtons = [
                              <Button
                                className="ListEvents__ShowDetailsButton"
                                icon={(
                                  <InfoCircleOutlined
                                    className="ListEvents__ShowDetailsButtonIcon"
                                  />
                                )}
                                title="Visualizar detalhes do evento"
                                type="link"
                                loading={detailsBeingFetched.includes(item.id)}
                                disabled={
                                  detailsBeingFetched.length > 0
                                  && (detailsBeingFetched.includes(item.id) === false)
                                }
                                onClick={
                                  (): void => {
                                    showEventDetails(item.id);
                                  }
                                }
                              />,
                            ];

                            if (
                              userType === 'COOPERCITRUS'
                              && (userPermissions || []).includes('Editar evento')
                            ) {
                              actionButtons.push(
                                <Button
                                  className="ListEvents__EditButton"
                                  icon={(
                                    <EditOutlined
                                      className="ListEvents__EditButtonIcon"
                                    />
                                  )}
                                  title="Editar detalhes do evento"
                                  type="link"
                                  onClick={
                                    (): void => {
                                      navigate(`/events/${item.id}/edit`);
                                    }
                                  }
                                />,
                              );
                            }

                            if (
                              userType === 'COOPERCITRUS'
                              && (userPermissions || []).includes('Cancelar evento')
                              && (item.isCancelled === false || item.isCancelled == null)
                            ) {
                              actionButtons.push(
                                <Button
                                  className="ListEvents__CancelButton"
                                  icon={(
                                    <CloseCircleOutlined
                                      className="ListEvents__CancelButtonIcon"
                                    />
                                  )}
                                  title="Cancelar evento"
                                  type="link"
                                  loading={idsBeingCancelled.includes(item.id)}
                                  onClick={
                                    (): void => {
                                      Modal.confirm({
                                        content: 'Deseja realmente cancelar o evento?',
                                        okText: 'Sim',
                                        cancelText: 'Não',

                                        onOk: (): void => {
                                          cancelEvent(item.id);
                                        },

                                        onCancel: (): void => {
                                          // do nothing.
                                        },
                                      });
                                    }
                                  }
                                />,
                              );
                            }

                            return (
                              <List.Item
                                className="ListEvents__EventsOnDateListItem"
                                actions={actionButtons}
                                style={{
                                  textDecoration: (item?.isCancelled === true) ? 'line-through' : 'none',
                                }}
                              >
                                {`[${moment(item.initialDate).utc().format('HH:mm')} - ${moment(item.finalDate).utc().format('HH:mm')}] - ${item.isCancelled ? '[CANCELADO] - ' : ''} ${item.name}`}
                              </List.Item>
                            );
                          }
                        }
                      />
                    </Col>
                  </Row>
                </Form>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <Modal
        className="ListEvents__DetailsModal"
        visible={visibleModalDetails}
        onOk={
          (): void => {
            setVisibleModalDetails(false);
            setDetailsData({});
          }
        }
        onCancel={
          (): void => {
            setVisibleModalDetails(false);
            setDetailsData({});
          }
        }
      >
        <Row
          gutter={[24, 24]}
          className="ListEvents__DetailsModalContentRow"
        >
          <Col
            xs={{ span: 24 }}
            className="ListEvents__DetailsModalContentColumn"
          >
            <Descriptions
              layout="vertical"
              className="ListEvents__DetailsModalDescription"
              title={
                (
                  <Typography.Title
                    level={4}
                    className="ListEvents__DescriptionTitle"
                  >
                    Detalhes
                  </Typography.Title>
                )
              }
            >
              <Descriptions.Item
                label="Nome do evento"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__EventNameDescriptionItem"
              >
                {detailsData?.name || '-'}
              </Descriptions.Item>

              <Descriptions.Item
                label="Descrição"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__DescriptionDescriptionItem"
              >
                {detailsData?.description || '-'}
              </Descriptions.Item>

              <Descriptions.Item
                label="Link"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__DescriptionDescriptionItem"
              >
                {
                  (
                    detailsData?.link != null
                      ? (
                        <Button
                          style={{
                            whiteSpace: 'normal',
                            textAlign: 'left',
                          }}
                          type="link"
                          onClick={
                            (): void => {
                              if (detailsData?.link != null) {
                                window.open(detailsData?.link, '_blank');
                              }
                            }
                          }
                          className="ListEvents__Hyperlink"
                        >
                          {
                            (
                              detailsData?.link != null && detailsData?.linkName
                                ? detailsData?.linkName
                                : detailsData?.link != null && detailsData?.linkName == null
                                  ? detailsData?.link
                                  : '-'
                            )
                          }
                        </Button>
                      )
                      : '-'
                  )
                }
              </Descriptions.Item>

              <Descriptions.Item
                label="Data"
                className="ListEvents__AllFormLabels ListEvents__DateDescriptionItem"
              >
                {
                  detailsData?.initialDate
                    ? moment(detailsData?.initialDate).utc().format('DD/MM/YYYY')
                    : '-'
                }
              </Descriptions.Item>

              <Descriptions.Item
                label="Cancelado"
                span={2}
                className="ListEvents__AllFormLabels ListEvents__IsCancelledDescriptionItem"
              >
                {
                  detailsData?.isCancelled
                    ? 'Sim'
                    : 'Não'
                }
              </Descriptions.Item>

              <Descriptions.Item
                label="Início"
                className="ListEvents__AllFormLabels ListEvents__StartTimeDescriptionItem"
              >
                {
                  detailsData?.initialDate
                    ? moment(detailsData?.initialDate).utc().format('HH:mm')
                    : '-'
                }
              </Descriptions.Item>

              <Descriptions.Item
                label="Final"
                span={2}
                className="ListEvents__AllFormLabels ListEvents__EndTimeDescriptionItem"
              >
                {
                  detailsData?.finalDate
                    ? moment(detailsData?.finalDate).utc().format('HH:mm')
                    : '-'
                }
              </Descriptions.Item>

              <Descriptions.Item
                label="Endereço"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__AddressDescriptionItem"
              >
                {showAddress()}
              </Descriptions.Item>

              <Descriptions.Item
                label="Unidade de negócio responsável (Filial)"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__SubsidiaryDescriptionItem"
              >
                {detailsData?.branchCoopercitrus?.map((element) => {
                  return `${element.name}, `;
                }) || '-'}
              </Descriptions.Item>

              <Descriptions.Item
                label="Cultura"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__CultureDescriptionItem"
              >
                {detailsData?.culture || '-'}
              </Descriptions.Item>

              <Descriptions.Item
                label="Observações"
                span={3}
                className="ListEvents__AllFormLabels ListEvents__CommentsDescriptionItem"
              >
                {detailsData?.comments || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

ListEvents.propTypes = {};
ListEvents.defaultProps = {};

export default ListEvents;
